import '@/ui/app.css';
import 'react-circular-progressbar/dist/styles.css';
import type { AppProps } from 'next/app';
import { NextSeo } from 'next-seo';
import { getCorporateconfig } from '@/modules/config';
import { i18n } from '@/infrastructure/locales/i18n';
import { MoneyStoreProvider } from '@/infrastructure/zustand/moneyStoreProvider';
import { LanguageStoreProvider } from '@/infrastructure/zustand/languageStoreProvider';
import { CorporateStoreProvider, useCorporateStore } from '@/infrastructure/zustand/corporateStoreProvider';
import { type ReactNode, useEffect } from 'react';
import type { Campaign } from '@/infrastructure/zustand/types/campaign.interface';
import type { Metadata } from '@/infrastructure/zustand/types/metadata.interface';
import type { Merchant } from '@/infrastructure/zustand/types/merchant.interface';
import { useRouter } from 'next/router';
import type { IndexConfig, CampaignConfig } from '@/modules/config.interface';
import Script from 'next/script';
import { useCookies } from 'react-cookie';
import { GOOGLE_RECAPTCHA_SITE_KEY } from '@/modules/constants';
import { I18nextProvider, useSSR } from 'react-i18next';
import { transformToLocale } from '@/infrastructure/zustand/languageStore';
import { AppCacheProvider } from '@mui/material-nextjs/v15-pagesRouter';
import { CorporateTheme } from '@/ui/CorporateTheme';

interface ExchangeRate {
	[Code: string]: number;
}

interface PageProps {
	seo?: {
		title: string;
		description: string;
		image: string;
	};
	exchangeRates: ExchangeRate;
	campaigns: Campaign[];
	metadata: Metadata;
	merchant: Merchant;
	currentLangCode: string;
	currency: string;
}

export default function App(props: AppProps) {
	const { appConfig } = getCorporateconfig(props.pageProps.site);
	const [cookies] = useCookies(['currency']);

	useEffect(() => {
		// Remove the server-side injected CSS.
		const jssStyles = document.querySelector('#jss-server-side');
		if (jssStyles) {
			jssStyles.parentNode!.removeChild(jssStyles);
		}
	}, []);

	const { seo } = props.pageProps as PageProps;

	const title = seo?.title ?? 'Worldcoo | Small donations for big causes';
	const description = seo?.description ?? '';

	if (/*pageProps.site === 'patagonia' || */ props.pageProps.statusCode === 404) {
		return <props.Component {...props.pageProps} />;
	}

	return (
		<AppCacheProvider {...props}>
			<I18nextProvider i18n={i18n}>
				<Script defer src={`https://www.google.com/recaptcha/api.js?render=${GOOGLE_RECAPTCHA_SITE_KEY}`} />
				<Script defer src="https://86118434d98d.eu-west-1.sdk.awswaf.com/86118434d98d/76d9b5adddd0/challenge.js" />

				<NextSeo
					title={title}
					description={description}
					openGraph={{
						title,
						description,
						url: typeof window !== 'undefined' ? window.location.origin : '',
						images: [{ url: seo?.image ?? '' }],
					}}
					twitter={{
						handle: '@handle',
						site: '@site',
						cardType: 'summary_large_image',
					}}
				/>

				<LanguageStoreProvider currentLangCode={props.pageProps.currentLangCode}>
					<MoneyStoreProvider
						defaultCurrency="EUR"
						exchangeRates={props.pageProps.exchangeRates}
						currency={cookies.currency}
					>
						<CorporateStoreProvider
							corporateId={appConfig.corporateId}
							metadata={props.pageProps.metadata}
							merchant={props.pageProps.merchant}
						>
							<WithDataLayerComponent {...props.pageProps}>
								<props.Component {...props.pageProps} />
							</WithDataLayerComponent>
						</CorporateStoreProvider>
					</MoneyStoreProvider>
				</LanguageStoreProvider>
			</I18nextProvider>
		</AppCacheProvider>
	);
}

function WithDataLayerComponent(props: (IndexConfig | CampaignConfig) & { children: ReactNode }) {
	// Workaround for useSSR error not working well in server side
	// See: https://github.com/i18next/react-i18next/issues/823
	if (!('browser' in process)) {
		i18n.initializedLanguageOnce = false;
	}

	const { campaignInfoConfig } = getCorporateconfig(props.site);

	// TODO: for now we're doing this to mimic correos behavior
	const showDonateButton = campaignInfoConfig.showDonateButton;

	useSSR(i18n.store.data, transformToLocale(props.currentLangCode));

	const themeBaseColor = useCorporateStore((state) => state.metadata.themeBaseColor);

	const router = useRouter();

	const campaign = 'campaign' in props ? props.campaign : undefined;

	useEffect(() => {
		if (
			(router.pathname.includes('donate') && (campaign?.finished || showDonateButton === false)) ||
			(props.campaignId && !campaign)
		) {
			router.replace('/');
		}
	}, [router.pathname, campaign?.finished, props.campaignId, campaign, router, showDonateButton]);
	if (
		(router.pathname.includes('donate') && (campaign?.finished || showDonateButton === false)) ||
		(props.campaignId && !campaign)
	) {
		return null;
	}

	const config: {
		fontFamily?: 'Open Sans' | 'titillium';
		color?: string;
	} =
		props.site === 'generaloptica' ? { color: '#e3002b' } : props.site === 'repsol' ? { fontFamily: 'titillium' } : {};

	return (
		<CorporateTheme color={config.color ?? themeBaseColor} fontFamily={config.fontFamily ?? 'Open Sans'}>
			{props.children}
		</CorporateTheme>
	);
}
